import { Component, OnInit, Input, ContentChildren, QueryList, AfterViewInit, OnChanges,
  Output, EventEmitter, AfterContentChecked } from '@angular/core';
import { trigger, transition, query, animate, keyframes, style, group } from '@angular/animations';
import { RDTabsComponent } from './../tabs.component';
import { RDDarkService } from './../../dark.service';

@Component({
  selector: 'rd-tabs-group',
  template: `<div class="tabsContainer {{color}}Tabs" [class.rdDark]="dark">
  <div class="tabsHeader">
    <div class="arrow" *ngIf="tabs.length > displayedTitleNumber" (click)="changeTitlesIndex(titleIndex - displayedTitleNumber)">
      <span><i class="fas fa-angle-left fa-lg"></i></span>
    </div>
    <div class="tabsTitleBar" [@fadeAnimation]="titleIndex">
      <div class="tabTitle" *ngFor="let title of titleList; let i = index" [style.minWidth.%]="100 / displayedTitleNumber"
      [class.active]="displayedIndex === i + titleIndex" (click)="changeDisplayedIndex(i + titleIndex)">
        {{title}}
      </div>
    </div>
    <div class="arrow" *ngIf="tabs.length > displayedTitleNumber" (click)="changeTitlesIndex(titleIndex + displayedTitleNumber)">
      <span><i class="fas fa-angle-right fa-lg"></i></span>
    </div>
  </div>

  <div class="tabContent" [@contentAnimation]="displayedIndex">
    <ng-content></ng-content>
  </div>
</div>
`,
  styles: [`.tabsContainer{width:100%;--main-color:#ba9e5a;--text-color:black;--hover-color:#f1f1f1;color:var(--text-color);transition:all .3s linear}.tabsContainer .tabsHeader{width:100%;display:flex;justify-content:space-between}.tabsContainer .tabsHeader .tabsTitleBar{flex:1;display:flex;flex-wrap:nowrap;overflow:hidden}.tabsContainer .tabsHeader .tabsTitleBar .tabTitle{flex:1;box-sizing:border-box;padding:6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-align:center;cursor:pointer;border-bottom:1px solid #e3e3e3;transition:border .3s ease-in-out}.tabsContainer .tabsHeader .tabsTitleBar .tabTitle.active{border-bottom-width:2px;border-bottom-color:var(--main-color)}.tabsContainer .tabsHeader .tabsTitleBar .tabTitle:hover{background-color:var(--hover-color);color:var(--main-color)}.tabsContainer .tabsHeader .arrow{padding:6px;box-sizing:border-box;position:relative;cursor:pointer}.tabsContainer .tabsHeader .arrow:hover{background-color:var(--hover-color)}.tabsContainer.greenTabs{--main-color:#72ba5a}.tabsContainer.redTabs{--main-color:#ba5a5a}.tabsContainer.blueTabs{--main-color:#5a5aba}.tabsContainer.blue-lightTabs{--main-color:#5aa2ba}.tabsContainer.purpleTabs{--main-color:#a25aba}.tabsContainer.orangeTabs{--main-color:#ba8a5a}.tabsContainer.blackTabs{--main-color:black}.tabsContainer.rdDark{--text-color:white;--hover-color:#07384c}`],
  animations: [
    trigger('fadeAnimation', [
      transition('* => *', [
        group([query(':enter', animate('.5s linear', keyframes([
            style({opacity: 0, offset: 0}),
            style({opacity: 1, offset: 1.0}),
          ])), {optional: true}),
        query(':leave', animate('.5s linear', keyframes([
            style({opacity: 1, offset: 0}),
            style({opacity: 0, offset: 1.0}),
          ])), {optional: true})])
      ])
    ]),
    trigger('contentAnimation', [
      transition(':increment', [
        query(':enter', animate('.5s linear', keyframes([
            style({opacity: 0, transform: 'translateX(100%)', offset: 0}),
            style({opacity: 1, transform: 'translateX(0)', offset: 1.0}),
          ])), {optional: true})
      ]),
      transition(':decrement', [
        query(':enter', animate('.5s linear', keyframes([
            style({opacity: 0, transform: 'translateX(-100%)', offset: 0}),
            style({opacity: 1, transform: 'translateX(0)', offset: 1.0}),
          ])), {optional: true})
      ])
    ])
  ]
})
export class RDTabsGroupComponent implements OnInit, AfterViewInit, AfterContentChecked, OnChanges {

  @Input() displayedTitleNumber = 5;
  @Input() displayedIndex = 0;
  @Input() color = 'gold';

  @Output() displayedIndexChange: EventEmitter<any> = new EventEmitter();

  @ContentChildren(RDTabsComponent) tabs: QueryList<RDTabsComponent>;

  public titleIndex = 0;
  public dark = false;

  constructor(private darkService: RDDarkService) { }

  ngOnInit() {
    this.darkService.getDarkElement().subscribe(() => {
      this.dark = this.darkService.isDark();
    });
    this.dark = this.darkService.isDark();
  }

  ngAfterViewInit() {
    this.setView();
  }

  ngOnChanges(changes) {
    if (this.tabs) {
      if (this.displayedIndex > this.tabs.length) {
        this.displayedIndex = this.tabs.length - 1;
        this.displayedIndexChange.emit(this.displayedIndex);
      }
      if (this.titleIndex + this.displayedTitleNumber > this.tabs.length) {
        this.changeTitlesIndex(this.tabs.length - this.displayedTitleNumber);
      }
      this.setView();
    }
  }

  ngAfterContentChecked() {
    if (this.displayedIndex > this.tabs.length) {
      this.displayedIndex = this.tabs.length - 1;
      this.displayedIndexChange.emit(this.displayedIndex);
    }
    if (this.titleIndex + this.displayedTitleNumber > this.tabs.length) {
      this.changeTitlesIndex(this.tabs.length - this.displayedTitleNumber);
    }
    this.setView();
  }

  get titleList() {
    const titles = [];
    this.tabs.forEach((tab, index) => {
      if (index >= this.titleIndex && index < this.titleIndex + this.displayedTitleNumber) {
        titles.push(tab.title);
      }
    });
    return titles;
  }

  changeTitlesIndex(value) {
    this.titleIndex = value;
    if (this.titleIndex < 0) {
      this.titleIndex = 0;
    } else if (this.titleIndex + this.displayedTitleNumber > this.tabs.length) {
      this.titleIndex = this.tabs.length - this.displayedTitleNumber;
    }
  }

  changeDisplayedIndex(value) {
    if (value !== this.displayedIndex) {
      this.displayedIndex = value;
      this.displayedIndexChange.emit(value);
      this.setView();
    }
  }

  setView() {
    this.tabs.forEach((tab, index) => {
      if (index === this.displayedIndex && tab.display === false) {
        Promise.resolve(null).then(() => tab.display = true);
        tab.changeDisplay.emit({index: index, state: true});
      } else if (index !== this.displayedIndex && tab.display === true) {
        Promise.resolve(null).then(() => tab.display = false);
        tab.changeDisplay.emit({index: index, state: false});
      }
    });
  }

}
